/**
 * session.ts — the per-visit analytics session id (STORY-009-003).
 *
 * The session id is a frontend-generated, stable-per-visit identifier
 * (TRD §11.2). It is persisted in `sessionStorage` — NOT `localStorage` —
 * so it survives an in-tab reload but dies when the tab closes, which is
 * exactly the "one id per visit" semantics the ingestion endpoint expects
 * (`AnalyticsBatchEventEnvelope.session_id`, STORY-009-002).
 *
 * Why `sessionStorage` is allowed here (and not a guardrail breach): the
 * localStorage/sessionStorage ban in REQ-PRD-§11.2-A is AUTH-specific — it
 * forbids persisting a JWT / access token / refresh token to web storage.
 * This value is an opaque, non-PII, non-auth UUID. The shared ESLint
 * guardrail (`@garden/config-eslint`) only trips on `setItem` calls whose
 * key matches auth-token names; `garden.session_id` does not, by design.
 *
 * The id is a v4 UUID from `crypto.randomUUID()` (React 19's baseline-2025
 * browser matrix guarantees it; jsdom provides it under Vitest). A
 * non-DOM / no-`sessionStorage` context degrades to a fresh in-memory id
 * so importing this in a pure test never throws.
 */

/** The `sessionStorage` key the visit id lives under. Non-auth, non-PII. */
export const SESSION_ID_STORAGE_KEY = "garden.session_id" as const;

/**
 * Process-local fallback id, used only when `sessionStorage` is
 * unavailable (e.g. a privacy mode that throws on access, or a non-DOM
 * test). Kept module-level so repeated calls in such a context return a
 * stable value rather than minting a new id every call.
 */
let inMemoryFallbackId: string | null = null;

/** Mint a fresh UUID, preferring the Web Crypto API. */
function mintId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  // Extremely defensive: a context without Web Crypto. Not expected on the
  // supported browser matrix; this keeps a pure import from throwing.
  inMemoryFallbackId ??= `sess-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
  return inMemoryFallbackId;
}

/**
 * Resolve the stable session id for this visit, creating + persisting one
 * on first call. Reuses the persisted id on an in-tab reload (AC#7).
 */
export function ensureSessionId(): string {
  if (typeof sessionStorage === "undefined") {
    inMemoryFallbackId ??= mintId();
    return inMemoryFallbackId;
  }
  try {
    const existing = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
    if (existing !== null && existing.length > 0) {
      return existing;
    }
    const fresh = mintId();
    sessionStorage.setItem(SESSION_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // `sessionStorage` access can throw (Safari private mode, blocked
    // storage). Fall back to a stable in-memory id for this visit.
    inMemoryFallbackId ??= mintId();
    return inMemoryFallbackId;
  }
}

/**
 * Reset the in-memory fallback id. FOR TESTS ONLY — lets a test that
 * stubs `sessionStorage` away assert fresh-id behaviour deterministically.
 */
export function __resetSessionIdForTests(): void {
  inMemoryFallbackId = null;
}
